/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useLanguage } from '@/providers/LanguageProvider';
import { AppListItem } from 'types/app-list';
import { FilterType, MeasuresData } from 'types/measures-api-data';

import { cn } from '@/lib/utils';

import { CustomButton, SearchInput } from '../common';
import { NotFoundMessage } from '../not-found';
import { OnboardingMesage } from '../not-found/onboarding-message';
import { AppCardsList } from './app-cards-list';

interface CustomApplicationsProps {
  apps: AppListItem[];
  locale: string;
  className?: string;
  measuresData: MeasuresData;
  loading: boolean;
  activeFilter: FilterType;
}

const CustomApplications = ({
  apps,
  locale,
  className,
  showExamples,
  measuresData,
  activeFilter,
  loading,
}: CustomApplicationsProps & { showExamples: boolean }) => {
  const { messages } = useLanguage();
  const router = useRouter();
  const langData = messages?.General;

  const [searchQuery, setSearchQuery] = useState('');

  // If there are no apps — show onboarding message without search and + new button
  if (apps.length === 0) {
    return (
      <div className={cn('w-2/3', className)}>
        <h3 className="text-h2 text-body-primary">
          {messages?.DashboardPage?.title?.custom_applications}
        </h3>
        <OnboardingMesage locale={locale} showExamples={showExamples} />
      </div>
    );
  }
  const filteredApps = apps.filter((app) =>
    app.display_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className={cn('w-2/3', className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-h2 text-body-primary">
            {messages?.DashboardPage?.title?.custom_applications}
          </h3>
          {filteredApps.length > 0 && (
            <span className="flex items-center justify-center rounded-full bg-[#40565A] px-2 py-1 text-body text-body-subtle">
              {filteredApps.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <SearchInput
            placeholder={langData?.search_by_name}
            onChange={(e) => setSearchQuery(e.target.value)}
            value={searchQuery}
            onClear={() => setSearchQuery('')}
            className="h-7"
          />
          <CustomButton
            // id is necessary for tour-guide
            id="step-2"
            customVariant="primary"
            customSize="regular"
            className="flex h-[28px] items-center gap-1 rounded-[2px] bg-fill-button-rest px-4 py-1.5 text-btn-label text-body-primary"
            onClick={() => router.push(`/${locale}/apps/new-app`)}
          >
            <Image
              src="/shared-icons/Add.svg"
              width={16}
              height={16}
              alt="Add Icon"
            />
            {messages?.DashboardPage?.new_app}
          </CustomButton>
        </div>
      </div>
      {filteredApps.length === 0 ? (
        <NotFoundMessage
          title={messages?.DashboardPage?.no_applications_found}
          message={messages?.DashboardPage?.no_results_on_searching}
        />
      ) : (
        <div
          className={`mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 ${showExamples ? '2xl:grid-cols-3' : 'lg:grid-cols-3 [@media(min-width:1440px)]:grid-cols-4'}`}
        >
          <AppCardsList
            apps={filteredApps}
            locale={locale}
            measuresData={measuresData}
            activeFilter={activeFilter}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
};

export default CustomApplications;
